import React, { FC, memo, useRef, useMemo } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Button, headingType, size, Heading, RichText, removeParentTagRTE } from '@marriott/mi-ui-library';

import { CobrandWebSmallBannerProps } from './CobrandWebSmallBanner.types';
import { StyledCobrandWebSmallBanner } from './CobrandWebSmallBanner.styles';
import { replaceWhitespaceAndAddBtn } from '../../modules/utils';
import { StringBoolean, ButtonStyleClass, CTAButtonType } from '../../modules/constants/constants';
import { CobrandImage } from '../../molecules/CobrandImage/CobrandImage';
import { CardCompareCheckbox } from '../../molecules/CardCompareCheckbox/CardCompareCheckbox';

export const cobrandwebsmallbannerConfig = {
  emptyLabel: 'CobrandWebSmallBanner',
  isEmpty: () => true,
  resourceType: `/components/content/cobrandwebsmallbanner`,
};

/**
 * CobrandWebSmallBanner Component
 * Displays a card banner with image, header, description, CTA button, and optional comparison checkbox
 *
 * Performance optimizations:
 * - Memoized with React.memo
 * - useMemo for computed values
 * - Uses CardCompareCheckbox molecule component
 */
export const CobrandWebSmallBannerComp: FC<CobrandWebSmallBannerProps> = memo<CobrandWebSmallBannerProps>(
  ({
    openInNewTab = false,
    ctaType,
    ctaLink,
    ctaLinkText,
    header,
    headerTag,
    descriptionText,
    descriptionTextMobile,
    trackingProperties,
    styleclass,
    componentId,
    dynamicMedia,
    openInNewWindowAriaLabel = '',
    customDescriptionClass,
    customContainerClass,
    customImageClass,
    cardCode,
    cardHeader,
    cardType,
    imagePathWithoutBadge,
    imagePathWithBadge,
    isComparisonTrayEnabled,
  }) => {
    // Memoized computed values to prevent unnecessary recalculations
    const hasHeader = useMemo(() => Boolean(header?.length), [header]);
    const hasDescriptionText = useMemo(() => Boolean(descriptionText?.length), [descriptionText]);
    const hasDescriptionTextMobile = useMemo(() => Boolean(descriptionTextMobile?.length), [descriptionTextMobile]);
    const isComparisonEnabled = useMemo(
      () => String(isComparisonTrayEnabled) === StringBoolean.TRUE,
      [isComparisonTrayEnabled]
    );

    // Clean RTE text - memoized to avoid re-processing
    const headerRTEText = useMemo(
      () =>
        hasHeader && header && (header.includes('<') || header.includes('>')) ? removeParentTagRTE(header) : header,
      [hasHeader, header]
    );

    // Image path with fallback - memoized
    const imagePath = useMemo(
      () => imagePathWithBadge || dynamicMedia?.assetPath,
      [imagePathWithBadge, dynamicMedia?.assetPath]
    );

    const imagePathForCheckbox = useMemo(
      () => imagePathWithoutBadge || imagePathWithBadge,
      [imagePathWithoutBadge, imagePathWithBadge]
    );

    // Refs for DOM access
    const buttonRef = useRef<HTMLDivElement>(null);

    // Button style computation - memoized
    const buttonTypeClasses = useMemo(() => {
      if (!ctaType) return '';

      switch (ctaType) {
        case CTAButtonType.PRIMARY:
          return openInNewTab ? ButtonStyleClass.PRIMARY_EXTERNAL : ButtonStyleClass.PRIMARY;
        case CTAButtonType.SECONDARY:
          return openInNewTab ? ButtonStyleClass.SECONDARY_EXTERNAL : ButtonStyleClass.SECONDARY;
        default:
          return '';
      }
    }, [ctaType, openInNewTab]);

    // Button ID for checkbox - memoized
    const buttonId = useMemo(() => replaceWhitespaceAndAddBtn(componentId), [componentId]);

    return (
      <StyledCobrandWebSmallBanner
        data-component-name="o-cobrand-CobrandWebSmallBanner"
        data-testid="cobrand-CobrandWebSmallBanner"
        id={componentId}
        className={clsx('col-12 col-sm-8 col-md-12 px-md-0', styleclass)}
      >
        <div className={clsx('card-container t-background-color', customContainerClass)}>
          {/* Card Image */}
          <div className={clsx('card-img-container', customImageClass)}>
            <CobrandImage defaultImageURL={imagePath} altText={dynamicMedia?.altText} customClass="card-img-styles" />
          </div>
          <div
            className={clsx(
              'flexible-card-content d-flex flex-grow-1 justify-content-between align-items-center flex-column flex-md-row'
            )}
          >
            {/* Header and Description */}
            <div className="flexible-card-content-container">
              <div className="flexible-card">
                {hasHeader && (
                  <Heading
                    customClass="mb-1 mb-md-0 ml-md-5"
                    variation={headingType.subtitle}
                    fontSize={size.large}
                    element={headerTag}
                    dangerouslySet={headerRTEText}
                  />
                )}
                {hasDescriptionText && descriptionText && (
                  <RichText
                    text={descriptionText}
                    componentId={`${componentId}-DescriptionText`}
                    customClass={clsx(
                      'd-none d-sm-block pr-md-2 t-font-alt-s ml-md-5',
                      { 'd-block': !hasDescriptionTextMobile },
                      customDescriptionClass
                    )}
                  />
                )}
                {hasDescriptionTextMobile && descriptionTextMobile && (
                  <RichText
                    text={descriptionTextMobile}
                    componentId={`${componentId}-DescriptionTextMobile`}
                    customClass="d-block d-sm-none pr-md-2 t-font-alt-s"
                  />
                )}
              </div>
            </div>
            {/* CTA and Comparison Checkbox Section */}
            <div className="d-flex justify-content-center align-items-center flex-column flex-lg-row">
              {ctaLinkText && (
                <Button
                  className={clsx(buttonTypeClasses, 'flexible-click m-button-s text-center mt-3 mb-4 my-lg-0 m-lg-0')}
                  href={ctaLink}
                  isLink
                  target={openInNewTab ? '_blank' : '_self'}
                  trackingProperties={trackingProperties}
                  rel={openInNewTab ? 'noopener noreferrer' : undefined}
                  buttonCopy={ctaLinkText}
                  linkAriaLabelOpenNewWindow={openInNewWindowAriaLabel}
                  ref={buttonRef}
                />
              )}
              {isComparisonEnabled && cardHeader && buttonId && cardCode && cardType && imagePathForCheckbox && (
                <CardCompareCheckbox
                  cardCode={cardCode}
                  cardType={cardType}
                  imagePath={imagePathForCheckbox}
                  cardHeader={cardHeader}
                  componentId={buttonId}
                  customClass={'ml-0 ml-lg-5 mb-5 mb-md-0'}
                />
              )}
            </div>
          </div>
        </div>
      </StyledCobrandWebSmallBanner>
    );
  }
);

CobrandWebSmallBannerComp.displayName = 'CobrandWebSmallBannerComp';

export const CobrandWebSmallBanner = (props: CobrandWebSmallBannerProps) => {
  return (
    <EditableComponent config={cobrandwebsmallbannerConfig} {...props}>
      <CobrandWebSmallBannerComp {...props} />
    </EditableComponent>
  );
};
